import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  RICE_LIGHTNING_BASE_URL,
  changeRiceLightningPassword,
  getRiceLightningAuthState,
  signInRiceLightningWithEmail,
  signOutRiceLightning,
  type RiceLightningUser,
} from "@rice-lightning/auth";

type AuthState = "loading" | "authorized" | "anonymous" | "unauthorized" | "error";
type AppState = "ready" | "built" | "foundation" | "planned";
type ProductItem = {
  name: string;
  short: string;
  description: string;
  href?: string;
  domain?: string;
  state: AppState;
};
type LauncherItem = ProductItem & {
  category: "apps" | "systems" | "resources";
};

const ROOT_HOSTNAME = "ricelightn.ing";
const DASHBOARD_PATH = "/dashboard";

const products: ProductItem[] = [
  { name: "RICE Tracking", short: "TR", description: "Tracking, measurement, pixels, analytics and installation health across sites and platforms.", href: "https://tracking.ricelightn.ing", domain: "tracking.ricelightn.ing", state: "ready" },
  { name: "RICE Queue", short: "QU", description: "Focused execution, handoffs and resumable work without losing the next action.", href: "https://queue.ricelightn.ing", domain: "queue.ricelightn.ing", state: "built" },
  { name: "RICE Verify", short: "VE", description: "Reusable identity, eligibility and verification workflows built for multiple consumers.", href: "https://verify.ricelightn.ing", domain: "verify.ricelightn.ing", state: "built" },
  { name: "RICE Reports", short: "RE", description: "Provider-agnostic reporting and normalized data products from connected systems.", href: "https://reports.ricelightn.ing", domain: "reports.ricelightn.ing", state: "built" },
  { name: "RICE Discord", short: "DI", description: "Discord automation, moderation and connected-feed control in one operating surface.", href: "https://discord.ricelightn.ing", domain: "discord.ricelightn.ing", state: "built" },
  { name: "RICE Inbox", short: "IN", description: "Omnichannel communications across messaging, email, voice and manual interactions.", href: "https://inbox.ricelightn.ing", domain: "inbox.ricelightn.ing", state: "built" },
  { name: "RICE Coverage", short: "CO", description: "Reusable insurance and coverage verification tooling for any workflow that needs it.", domain: "coverage.ricelightn.ing", state: "foundation" },
  { name: "RICE Sign", short: "SI", description: "Reusable document, agreement and electronic-signature workflows.", domain: "sign.ricelightn.ing", state: "planned" },
];

const launcherItems: LauncherItem[] = [
  ...products.map((item) => ({ ...item, category: "apps" as const })),
  { name: "RICE Lightning Repo", short: "GH", description: "Canonical implementation repository for the platform and its modular products.", href: "https://github.com/everydayrice/rice-lightning", state: "ready", category: "systems" },
  { name: "Cloudflare", short: "CF", description: "Primary edge runtime, routing, domains and R2 infrastructure.", href: "https://dash.cloudflare.com", state: "ready", category: "resources" },
  { name: "Neon", short: "NE", description: "Authentication, Postgres databases and durable application state.", href: "https://console.neon.tech", state: "ready", category: "resources" },
  { name: "Resend", short: "RS", description: "Transactional email infrastructure for RICE Lightning products.", href: "https://resend.com", state: "ready", category: "resources" },
];

const stateLabel: Record<AppState, string> = {
  ready: "Live",
  built: "Built",
  foundation: "Foundation",
  planned: "Planned",
};

function LightningMark({ small = false }: { small?: boolean }) {
  return <span className={`brand-orb${small ? " small" : ""}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" focusable="false">
      <path d="M13.7 1.9 5.2 13.1h5.5l-1 9 9.1-12h-5.6l.5-8.2Z" />
    </svg>
  </span>;
}

function Wordmark() {
  return <div className="wordmark"><LightningMark small/><div><strong>RICE</strong><span>LIGHTNING</span></div></div>;
}

function safeReturnTarget() {
  const raw = new URLSearchParams(window.location.search).get("return");
  if (raw) {
    try {
      const target = new URL(raw, RICE_LIGHTNING_BASE_URL);
      if (target.protocol === "https:" && (target.hostname === ROOT_HOSTNAME || target.hostname.endsWith(`.${ROOT_HOSTNAME}`))) return target.toString();
    } catch {
      // Ignore invalid return targets.
    }
  }

  if (window.location.hostname === ROOT_HOSTNAME && window.location.pathname === DASHBOARD_PATH) {
    return `${RICE_LIGHTNING_BASE_URL}${DASHBOARD_PATH}`;
  }

  return RICE_LIGHTNING_BASE_URL;
}

function errorMessage(error: unknown) {
  if (error && typeof error === "object" && "message" in error && typeof (error as { message?: unknown }).message === "string") {
    return (error as { message: string }).message;
  }
  return "Authentication could not be completed.";
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>("loading");
  const [user, setUser] = useState<RiceLightningUser | null>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<"all" | LauncherItem["category"]>("all");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [accessOpen, setAccessOpen] = useState(() => window.location.pathname === DASHBOARD_PATH || new URLSearchParams(window.location.search).has("return"));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  useEffect(() => {
    void getRiceLightningAuthState()
      .then((state) => {
        setUser(state.user);
        if (state.authorized) setAuth("authorized");
        else if (state.authenticated) setAuth("unauthorized");
        else setAuth("anonymous");
      })
      .catch(() => setAuth("error"));
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return launcherItems.filter((item) => {
      const categoryMatch = category === "all" || item.category === category;
      const searchMatch = !normalized || `${item.name} ${item.description} ${item.domain ?? ""}`.toLowerCase().includes(normalized);
      return categoryMatch && searchMatch;
    });
  }, [category, query]);

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail || !password || password.length < 8) {
      setLoginError("Enter your email and password.");
      return;
    }

    setLoginBusy(true);
    setLoginError("");
    const returnTo = safeReturnTarget();

    try {
      const result = await signInRiceLightningWithEmail(normalizedEmail, password, returnTo);
      if (result.error) {
        setLoginError("Invalid email or password.");
        return;
      }
      window.location.assign(returnTo);
    } catch (error) {
      setLoginError(errorMessage(error));
    } finally {
      setLoginBusy(false);
    }
  }

  async function logout() {
    await signOutRiceLightning();
    window.location.assign(RICE_LIGHTNING_BASE_URL);
  }

  async function changePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setPasswordBusy(true);
    try {
      const result = await changeRiceLightningPassword(currentPassword, newPassword);
      if (result.error) {
        setPasswordError(errorMessage(result.error));
        return;
      }
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Password updated successfully.");
      window.setTimeout(() => setSettingsOpen(false), 650);
    } catch (error) {
      setPasswordError(errorMessage(error));
    } finally {
      setPasswordBusy(false);
    }
  }

  if (auth === "loading") return <div className="boot-screen"><LightningMark/><p>Loading RICE Lightning</p></div>;

  const isDashboard = window.location.pathname === DASHBOARD_PATH;
  if (isDashboard && auth === "authorized") {
    return <div className="launcher-shell">
      <header className="launcher-header">
        <a href={RICE_LIGHTNING_BASE_URL} aria-label="RICE Lightning homepage"><Wordmark/></a>
        <nav className="dashboard-nav" aria-label="Dashboard navigation">
          <a href={RICE_LIGHTNING_BASE_URL}>Homepage</a>
          <button className="ghost-button" onClick={() => setSettingsOpen(true)}>Settings</button>
          <button className="ghost-button" onClick={logout}>Sign out</button>
        </nav>
      </header>

      <main className="launcher-content">
        <section className="dashboard-hero">
          <div>
            <p className="eyebrow">Private dashboard</p>
            <h1>Your tools.<br/>One surface.</h1>
            <p>Launch RICE Lightning products, inspect platform resources and move directly into the system you need.</p>
          </div>
          <div className="identity-card"><span className="online-dot"/><div><span>Signed in</span><strong>{user?.email}</strong></div></div>
        </section>

        <section className="toolbar">
          <label className="search"><span aria-hidden="true">⌕</span><input aria-label="Search apps, systems and resources" placeholder="Search apps, systems and resources" value={query} onChange={(event) => setQuery(event.target.value)}/></label>
          <div className="filters">{(["all", "apps", "systems", "resources"] as const).map((value) => <button key={value} className={category === value ? "active" : ""} onClick={() => setCategory(value)}>{value === "all" ? "All" : value[0].toUpperCase() + value.slice(1)}</button>)}</div>
        </section>

        <section className="map-grid" aria-label="RICE Lightning application map">
          {filtered.map((item) => {
            const disabled = !item.href;
            const body = <><div className="tile-top"><span className="tile-icon">{item.short}</span><span className={`status status-${item.state}`}>{stateLabel[item.state]}</span></div><div className="tile-copy"><h2>{item.name}</h2><p>{item.description}</p></div><div className="tile-foot"><span>{item.domain ?? item.category}</span><strong>{disabled ? "Not routed" : "Open ↗"}</strong></div></>;
            return disabled ? <article className="launch-tile disabled" key={item.name}>{body}</article> : <a className="launch-tile" href={item.href} key={item.name}>{body}</a>;
          })}
        </section>
      </main>

      {settingsOpen && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSettingsOpen(false); }}>
        <section className="modal-card settings-card" role="dialog" aria-modal="true" aria-labelledby="settings-title">
          <div className="settings-head"><div><p className="eyebrow">Account</p><h2 id="settings-title">Settings</h2></div><button className="icon-button" onClick={() => setSettingsOpen(false)} aria-label="Close settings">×</button></div>
          <div className="settings-section"><span className="settings-label">Signed-in identity</span><strong>{user?.email}</strong></div>
          <form className="password-form" onSubmit={changePassword}>
            <h3>Change password</h3>
            <label>Current password<input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" /></label>
            <label>New password<input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" /></label>
            <label>Confirm new password<input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" /></label>
            {passwordError && <p className="form-error">{passwordError}</p>}
            {passwordSuccess && <p className="form-success">{passwordSuccess}</p>}
            <button className="primary-button" type="submit" disabled={passwordBusy}>{passwordBusy ? "Updating…" : "Update password"}</button>
          </form>
        </section>
      </div>}
    </div>;
  }

  const unauthorized = auth === "unauthorized";
  const authError = auth === "error";

  return <div className="site-shell">
    <header className="site-header">
      <a href="#top" className="site-brand" aria-label="RICE Lightning home"><Wordmark/></a>
      <nav className="site-nav" aria-label="Primary navigation">
        <a href="#products">Products</a>
        <a href="#how-it-works">How it works</a>
        <a href="#platform">Platform</a>
      </nav>
      {auth === "authorized"
        ? <a className="header-cta" href={DASHBOARD_PATH}>Open dashboard <span>↗</span></a>
        : <button className="header-cta" onClick={() => setAccessOpen(true)}>Sign in <span>↗</span></button>}
    </header>

    <main id="top">
      <section className="public-hero section-shell">
        <div className="hero-copy">
          <p className="eyebrow">Independent software. One platform.</p>
          <h1>Build once.<br/><span>Use everywhere.</span></h1>
          <p className="hero-lede">RICE LIGHTNING is a platform for independent apps and tools built to solve real operational problems. Each product stands on its own first, then connects to the RICE business, project or system that needs it.</p>
          <div className="hero-actions">
            <a className="primary-link" href="#products">Explore products <span>↓</span></a>
            {auth === "authorized" ? <a className="secondary-link" href={DASHBOARD_PATH}>Open dashboard</a> : <button className="secondary-link" onClick={() => setAccessOpen(true)}>Private access</button>}
          </div>
        </div>
        <div className="hero-system" aria-label="RICE Lightning product model">
          <div className="system-kicker"><span className="pulse"/> RICE LIGHTNING</div>
          <div className="system-node core-node"><LightningMark/><div><strong>ONE PLATFORM</strong><span>Shared runtime + identity</span></div></div>
          <div className="system-line"/>
          <div className="system-products">
            <div className="mini-node"><span>TR</span><strong>Tracking</strong></div>
            <div className="mini-node"><span>QU</span><strong>Queue</strong></div>
            <div className="mini-node"><span>VE</span><strong>Verify</strong></div>
            <div className="mini-node"><span>+</span><strong>More</strong></div>
          </div>
          <div className="system-caption">Independent products. Connected intentionally.</div>
        </div>
      </section>

      <section className="definition section-shell" aria-labelledby="definition-title">
        <div className="section-intro">
          <p className="eyebrow">What it is</p>
          <h2 id="definition-title">A home for software that should not be buried inside one business.</h2>
        </div>
        <div className="principle-grid">
          <article><span>01</span><h3>Independent by design</h3><p>A capability becomes a real product with its own purpose, interface and boundary instead of a one-off feature hidden inside another application.</p></article>
          <article><span>02</span><h3>Shared where it matters</h3><p>Products can share authentication, infrastructure and platform services so the system stays simple without collapsing every tool into one giant app.</p></article>
          <article><span>03</span><h3>Connected where useful</h3><p>RICE companies and projects can become the first users of a product, then reuse that same product anywhere else it solves the same problem.</p></article>
        </div>
      </section>

      <section className="workflow section-shell" id="how-it-works" aria-labelledby="workflow-title">
        <div className="section-intro split-intro">
          <div><p className="eyebrow">How it works</p><h2 id="workflow-title">From operational need to reusable product.</h2></div>
          <p>RICE LIGHTNING keeps the business problem and the software product separate on purpose.</p>
        </div>
        <div className="workflow-track">
          <article><span className="step-number">01</span><div><strong>Find the recurring need</strong><p>A real workflow exposes a problem worth solving once instead of repeatedly.</p></div></article>
          <article><span className="step-number">02</span><div><strong>Build the product first</strong><p>The capability gets its own application boundary, UX and reusable logic.</p></div></article>
          <article><span className="step-number">03</span><div><strong>Connect Consumer #1</strong><p>The business or system that created the need adopts the product without owning its boundary.</p></div></article>
          <article><span className="step-number">04</span><div><strong>Reuse and expand</strong><p>The same product can serve new workflows, companies and users without being rebuilt.</p></div></article>
        </div>
      </section>

      <section className="products-section section-shell" id="products" aria-labelledby="products-title">
        <div className="section-intro split-intro">
          <div><p className="eyebrow">Products</p><h2 id="products-title">The RICE LIGHTNING suite.</h2></div>
          <p>A growing catalog of focused software products. Each has a distinct job; all can live on the same platform.</p>
        </div>
        <div className="product-grid">
          {products.map((product) => <article className="product-card" key={product.name}>
            <div className="product-card-top"><span className="product-icon">{product.short}</span><span className={`status status-${product.state}`}>{stateLabel[product.state]}</span></div>
            <div><h3>{product.name}</h3><p>{product.description}</p></div>
            <div className="product-card-foot"><span>{product.domain}</span>{auth === "authorized" && product.href ? <a href={product.href}>Open ↗</a> : <span>{product.href ? "Private app" : "In development"}</span>}</div>
          </article>)}
        </div>
      </section>

      <section className="platform-section section-shell" id="platform" aria-labelledby="platform-title">
        <div className="platform-panel">
          <div className="platform-copy"><p className="eyebrow">Platform architecture</p><h2 id="platform-title">One runtime.<br/>Modular applications.</h2><p>RICE LIGHTNING defaults to one primary Cloudflare Worker while each product remains modular in code and UX. Products split into separate runtimes only when there is a real reason to do it.</p></div>
          <div className="platform-stack">
            <div><span>Runtime</span><strong>Cloudflare Workers</strong><small>Edge routing + application delivery</small></div>
            <div><span>Identity + data</span><strong>Neon</strong><small>Neon Auth + Postgres</small></div>
            <div><span>Files</span><strong>Cloudflare R2</strong><small>Shared object storage with app namespaces</small></div>
            <div><span>Email</span><strong>Resend</strong><small>Transactional delivery via txn.ricelightn.ing</small></div>
            <div><span>Source</span><strong>GitHub</strong><small>everydayrice/rice-lightning</small></div>
          </div>
        </div>
      </section>

      <section className="closing section-shell">
        <div><p className="eyebrow">RICE LIGHTNING</p><h2>Build the capability once.<br/>Connect it wherever it belongs.</h2></div>
        {auth === "authorized" ? <a className="primary-link" href={DASHBOARD_PATH}>Open dashboard <span>↗</span></a> : <button className="primary-link" onClick={() => setAccessOpen(true)}>Private access <span>↗</span></button>}
      </section>
    </main>

    <footer className="site-footer section-shell"><Wordmark/><p>Independent software, tools and infrastructure built for reuse.</p><span>ricelightn.ing</span></footer>

    {accessOpen && auth !== "authorized" && <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && window.location.pathname !== DASHBOARD_PATH) setAccessOpen(false); }}>
      <section className="modal-card access-card" role="dialog" aria-modal="true" aria-labelledby="access-title">
        <div className="settings-head"><div className="modal-brand"><LightningMark small/><span>PRIVATE ACCESS</span></div>{window.location.pathname !== DASHBOARD_PATH && <button className="icon-button" onClick={() => setAccessOpen(false)} aria-label="Close sign in">×</button>}</div>
        <h2 id="access-title">{unauthorized ? "Account not authorized." : "Sign in to RICE LIGHTNING."}</h2>
        <p>{unauthorized ? "This authenticated account does not have access to the private RICE LIGHTNING dashboard." : "One RICE LIGHTNING identity unlocks the dashboard and connected private applications."}</p>
        {unauthorized ? <button className="secondary-button full-button" onClick={logout}>Sign out</button> : <form className="access-form" onSubmit={login}>
          <label>Email<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck={false}/></label>
          <label>Password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" autoFocus/></label>
          {(loginError || authError) && <p className="form-error">{loginError || "Authentication is temporarily unavailable."}</p>}
          <button className="primary-button" type="submit" disabled={loginBusy}>{loginBusy ? "Signing in…" : "Sign in"}</button>
        </form>}
      </section>
    </div>}
  </div>;
}
